import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material';
import { useUpdateCustomerFeedback } from '@/hooks/mutations/feedback/useUpdateCustomerFeedback';
import { getErrorMessage } from '@/utils/httpClient';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import type {
  MissingEquipmentReason,
  MissingEquipmentResponse,
  ProcessEfficiency,
  ServiceSatisfaction,
  StaffHelpfulness,
  UpdateCustomerFeedbackResponse,
} from '@/services/feedback/CustomerFeedbackService';

export interface CustomerFeedbackModalProps {
  open: boolean;
  transactionLogId?: string | null;
  customerName?: string;
  userEmail?: string;
  onClose: () => void;
  /** Called after the survey is successfully submitted (e.g. for standalone page). */
  onSubmitSuccess?: () => void;
  /** Called when the user explicitly clicks "Skip" (e.g. to enqueue a survey email). */
  onSkip?: () => void;
}

export const CustomerFeedbackModal: React.FC<CustomerFeedbackModalProps> = ({
  open,
  transactionLogId,
  customerName,
  userEmail,
  onClose,
  onSubmitSuccess,
  onSkip,
}) => {
  const [missingEquipmentResponse, setMissingEquipmentResponse] =
    useState<MissingEquipmentResponse | null>(null);
  const [missingEquipmentReason, setMissingEquipmentReason] =
    useState<MissingEquipmentReason | null>(null);
  const [missingEquipmentDetails, setMissingEquipmentDetails] = useState('');
  const [serviceSatisfaction, setServiceSatisfaction] = useState<ServiceSatisfaction | null>(null);
  const [staffHelpfulness, setStaffHelpfulness] = useState<StaffHelpfulness | null>(null);
  const [processEfficiency, setProcessEfficiency] = useState<ProcessEfficiency | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { mutate, isPending, reset } = useUpdateCustomerFeedback();
  const isViewer = useWriteGuard();

  useEffect(() => {
    if (!open) {
      setMissingEquipmentResponse(null);
      setMissingEquipmentReason(null);
      setMissingEquipmentDetails('');
      setServiceSatisfaction(null);
      setStaffHelpfulness(null);
      setProcessEfficiency(null);
      setErrorMessage(null);
      setSuccessMessage(null);
      reset();
    }
  }, [open, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Guard against Enter-key form submission bypassing the disabled button.
    if (isViewer) return;

    if (!transactionLogId) {
      setErrorMessage('Survey is not available for this interaction yet.');
      setSuccessMessage(null);
      return;
    }

    if (!missingEquipmentResponse) {
      setErrorMessage('Answer whether any required equipment was unavailable.');
      setSuccessMessage(null);
      return;
    }

    if (missingEquipmentResponse === 'yes' && !missingEquipmentReason) {
      setErrorMessage('Select why the equipment could not be received.');
      setSuccessMessage(null);
      return;
    }

    const trimmedMissingDetails = missingEquipmentDetails.trim();
    if (missingEquipmentResponse === 'yes' && trimmedMissingDetails.length === 0) {
      setErrorMessage('List the missing equipment before submitting.');
      setSuccessMessage(null);
      return;
    }

    if (!serviceSatisfaction) {
      setErrorMessage('Select how satisfied you were with the visit.');
      setSuccessMessage(null);
      return;
    }

    if (!staffHelpfulness) {
      setErrorMessage('Indicate whether the staff were helpful and professional.');
      setSuccessMessage(null);
      return;
    }

    if (!processEfficiency) {
      setErrorMessage('Rate the efficiency of the issue or turn-in process.');
      setSuccessMessage(null);
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);

    mutate(
      {
        transactionLogId,
        missingEquipmentResponse,
        missingEquipmentReason:
          missingEquipmentResponse === 'yes' ? (missingEquipmentReason ?? null) : null,
        missingEquipmentDetails: missingEquipmentResponse === 'yes' ? trimmedMissingDetails : null,
        serviceSatisfaction,
        staffHelpfulness,
        processEfficiency,
        customerName,
        userEmail,
      },
      {
        onSuccess: (response: UpdateCustomerFeedbackResponse) => {
          setSuccessMessage(response.message ?? 'Thanks for your feedback!');
          setMissingEquipmentResponse(null);
          setMissingEquipmentReason(null);
          setMissingEquipmentDetails('');
          setServiceSatisfaction(null);
          setStaffHelpfulness(null);
          setProcessEfficiency(null);
          onSubmitSuccess?.();
          handleClose();
        },
        onError: (error) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  const isSubmitting = isPending;
  const isInteractionReady = Boolean(transactionLogId);
  const surveyDisabled = !isInteractionReady || isSubmitting;

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Customer Service Survey</DialogTitle>
      <DialogContent dividers>
        <Stack component="form" spacing={3} onSubmit={handleSubmit}>
          <FormControl required disabled={surveyDisabled}>
            <FormLabel>
              Was there any equipment/clothing you are assigned/needed but couldn't receive today?
            </FormLabel>
            <RadioGroup
              value={missingEquipmentResponse ?? ''}
              onChange={(event) =>
                setMissingEquipmentResponse(event.target.value as MissingEquipmentResponse)
              }
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
              <FormControlLabel value="na" control={<Radio />} label="N/A" />
            </RadioGroup>
          </FormControl>

          {missingEquipmentResponse === 'yes' && (
            <Stack spacing={2}>
              <FormControl required disabled={surveyDisabled}>
                <FormLabel>If yes, choose a reason</FormLabel>
                <RadioGroup
                  value={missingEquipmentReason ?? ''}
                  onChange={(event) =>
                    setMissingEquipmentReason(event.target.value as MissingEquipmentReason)
                  }
                >
                  <FormControlLabel value="not_in_stock" control={<Radio />} label="Not in Stock" />
                  <FormControlLabel value="unauthorized" control={<Radio />} label="Unauthorized" />
                </RadioGroup>
              </FormControl>

              <TextField
                label="List or annotate the missing equipment"
                placeholder="Example: Size L OCP top, cold weather gloves"
                value={missingEquipmentDetails}
                onChange={(event) => setMissingEquipmentDetails(event.target.value)}
                multiline
                minRows={2}
                fullWidth
                required
                disabled={surveyDisabled}
              />
            </Stack>
          )}

          <FormControl required disabled={surveyDisabled}>
            <FormLabel>How satisfied were you with the service during your visit?</FormLabel>
            <RadioGroup
              value={serviceSatisfaction ?? ''}
              onChange={(event) =>
                setServiceSatisfaction(event.target.value as ServiceSatisfaction)
              }
            >
              <FormControlLabel value="excellent" control={<Radio />} label="Excellent" />
              <FormControlLabel value="good" control={<Radio />} label="Good" />
              <FormControlLabel value="fair" control={<Radio />} label="Fair" />
              <FormControlLabel value="poor" control={<Radio />} label="Poor" />
            </RadioGroup>
          </FormControl>

          <FormControl required disabled={surveyDisabled}>
            <FormLabel>Was the staff helpful and professional?</FormLabel>
            <RadioGroup
              value={staffHelpfulness ?? ''}
              onChange={(event) => setStaffHelpfulness(event.target.value as StaffHelpfulness)}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
              <FormControlLabel value="na" control={<Radio />} label="N/A" />
            </RadioGroup>
          </FormControl>

          <FormControl required disabled={surveyDisabled}>
            <FormLabel>How efficient was the issue or turn-in process?</FormLabel>
            <RadioGroup
              value={processEfficiency ?? ''}
              onChange={(event) => setProcessEfficiency(event.target.value as ProcessEfficiency)}
            >
              <FormControlLabel value="excellent" control={<Radio />} label="Excellent" />
              <FormControlLabel value="good" control={<Radio />} label="Good" />
              <FormControlLabel value="fair" control={<Radio />} label="Fair" />
              <FormControlLabel value="poor" control={<Radio />} label="Poor" />
              <FormControlLabel value="na" control={<Radio />} label="N/A" />
            </RadioGroup>
          </FormControl>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {successMessage && <Alert severity="success">{successMessage}</Alert>}

          {!isInteractionReady && !errorMessage && (
            <Alert severity="info">
              Survey captures once the service interaction is associated with a transaction log.
            </Alert>
          )}

          <Box display="flex" justifyContent="flex-end" gap={1}>
            {onSkip && (
              <Button
                onClick={() => {
                  onSkip();
                  handleClose();
                }}
                color="inherit"
                disabled={isSubmitting}
              >
                Skip
              </Button>
            )}
            <ViewerGuardedButton type="submit" variant="contained" disabled={surveyDisabled}>
              {isSubmitting ? 'Submitting…' : 'Submit Survey'}
            </ViewerGuardedButton>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
